import { logger } from '@/lib/logger';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { supabase } from '@/integrations/supabase/client';

/**
 * Native LocalNotifications scheduler — offline backup for medication reminders.
 *
 * Why this exists:
 *  - Server push (send-medication-reminders edge function) is the source of truth.
 *  - When the device is offline (airplane mode, no signal, FCM throttled), the
 *    server can't reach it. We pre-schedule a rolling 7-day window of OS-level
 *    LocalNotifications so reminders still fire.
 *
 * De-duplication contract (mirrors send-medication-reminders/index.ts):
 *  - Occurrence ID format: `${userId}:${prescriptionId}:${HH:MM}:${YYYY-MM-DD}`
 *    (date is in the *effective* timezone — same as the edge function).
 *  - Each native notification embeds occurrenceId in extra. On app foreground we
 *    cancel pending native notifications whose occurrenceId already appears in
 *    reminder_delivery_log (server already delivered → no double banner).
 *  - Notification IDs are a 31-bit hash of occurrenceId, so re-scheduling the
 *    same occurrence replaces (not duplicates) the pending native notification.
 *
 * Timezone semantics (mirrors edge function):
 *  - schedule_mode 'wall_clock' (default): fire at HH:MM in travel_timezone when
 *    travel_active, otherwise in profile.timezone (home).
 *  - schedule_mode 'anchored': always fire at HH:MM in profile.timezone (home),
 *    ignoring travel state. Use for medications tied to body-clock dosing.
 */

const ROLLING_DAYS = 7;
const CHANNEL_ID = 'medication-reminders';

interface ReminderTime {
  enabled: boolean;
  time: string; // HH:MM
}

interface PrescriptionRow {
  id: string;
  user_id: string;
  medication_name: string;
  dosage: string | null;
  reminders: ReminderTime[] | null;
  reminder_days_of_week: number[] | null;
  schedule_mode: 'wall_clock' | 'anchored' | null;
}

interface ProfileRow {
  id: string;
  timezone: string | null;
  travel_timezone: string | null;
  travel_active: boolean | null;
}

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** djb2 hash → 31-bit positive int (LocalNotifications id must fit Int32). */
function hashOccurrenceId(occurrenceId: string): number {
  let h = 5381;
  for (let i = 0; i < occurrenceId.length; i++) {
    h = ((h << 5) + h + occurrenceId.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 0x7fffffff;
}

/** Convert a wall-clock instant in `tz` to a real UTC Date. */
function zonedWallClockToDate(
  year: number,
  month: number, // 1-12
  day: number,
  hour: number,
  minute: number,
  tz: string,
): Date {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  // Two passes converges near DST transitions where the first-guess offset is
  // wrong by ±1h.
  let utcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 2; i++) {
    const parts = dtf.formatToParts(new Date(utcMs));
    const m: Record<string, string> = {};
    for (const p of parts) m[p.type] = p.value;
    const asTzMs = Date.UTC(
      Number(m.year),
      Number(m.month) - 1,
      Number(m.day),
      Number(m.hour) % 24,
      Number(m.minute),
      Number(m.second),
    );
    utcMs = utcMs - (asTzMs - utcMs);
  }
  return new Date(utcMs);
}

/** YYYY-MM-DD and weekday (0=Sun..6=Sat) for `now` in tz. */
function todayInTz(tz: string, now: Date = new Date()): { y: number; m: number; d: number; weekday: number } {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
  });
  const m: Record<string, string> = {};
  for (const p of dtf.formatToParts(now)) m[p.type] = p.value;
  const wmap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    y: Number(m.year),
    m: Number(m.month),
    d: Number(m.day),
    weekday: wmap[m.weekday] ?? 0,
  };
}

function resolveEffectiveTz(profile: ProfileRow, scheduleMode: PrescriptionRow['schedule_mode']): string {
  const home = profile.timezone || 'UTC';
  if (scheduleMode === 'anchored') return home;
  if (profile.travel_active && profile.travel_timezone) return profile.travel_timezone;
  return home;
}

async function ensureChannelAndPermission(): Promise<boolean> {
  try {
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') {
      const req = await LocalNotifications.requestPermissions();
      if (req.display !== 'granted') return false;
    }
    if (Capacitor.getPlatform() === 'android') {
      try {
        await LocalNotifications.createChannel({
          id: CHANNEL_ID,
          name: 'Medication reminders',
          description: 'Time-critical medication reminders',
          importance: 5,
          visibility: 1,
          vibration: true,
        });
      } catch {
        /* channel may already exist */
      }
    }
    return true;
  } catch (e) {
    logger.warn('[NativeReminders] permission/channel setup failed', e);
    return false;
  }
}

interface PlannedOccurrence {
  notificationId: number;
  occurrenceId: string;
  fireAt: Date;
  prescription: PrescriptionRow;
  reminderTime: string;
  scheduledDate: string; // YYYY-MM-DD in effective tz
  effectiveTz: string;
}

function planOccurrencesForUser(
  profile: ProfileRow,
  prescriptions: PrescriptionRow[],
  now: Date = new Date(),
): PlannedOccurrence[] {
  const planned: PlannedOccurrence[] = [];
  for (const presc of prescriptions) {
    const reminders = presc.reminders;
    if (!Array.isArray(reminders) || reminders.length === 0) continue;

    const tz = resolveEffectiveTz(profile, presc.schedule_mode);
    const allowedDays = presc.reminder_days_of_week;
    const dayFiltered = Array.isArray(allowedDays) && allowedDays.length > 0 && allowedDays.length < 7;

    // Anchor at local noon so a DST-shortened/lengthened day cannot skip a date.
    const today = todayInTz(tz, now);
    const noonAnchor = zonedWallClockToDate(today.y, today.m, today.d, 12, 0, tz);
    for (let offset = 0; offset < ROLLING_DAYS; offset++) {
      const future = new Date(noonAnchor.getTime() + offset * 86400_000);
      const { y, m, d, weekday } = todayInTz(tz, future);
      if (dayFiltered && !allowedDays!.includes(weekday)) continue;

      for (const r of reminders) {
        if (!r.enabled || !r.time) continue;
        const [hh, mm] = r.time.split(':').map(Number);
        if (Number.isNaN(hh) || Number.isNaN(mm)) continue;

        const fireAt = zonedWallClockToDate(y, m, d, hh, mm, tz);
        // Only schedule strictly future occurrences (skip ones already passed today).
        if (fireAt.getTime() <= now.getTime() + 30_000) continue;

        const scheduledDate = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const occurrenceId = `${presc.user_id}:${presc.id}:${r.time}:${scheduledDate}`;
        planned.push({
          notificationId: hashOccurrenceId(occurrenceId),
          occurrenceId,
          fireAt,
          prescription: presc,
          reminderTime: r.time,
          scheduledDate,
          effectiveTz: tz,
        });
      }
    }
  }
  return planned;
}

/** Cancel pending native notifications whose occurrenceId was already delivered by server push. */
async function cancelAlreadyDelivered(userId: string): Promise<void> {
  if (!isNative()) return;
  try {
    const pending = await LocalNotifications.getPending();
    if (!pending.notifications.length) return;

    const occurrenceIds = pending.notifications
      .map((n) => (n.extra as { occurrenceId?: string } | null)?.occurrenceId)
      .filter((v): v is string => typeof v === 'string');
    if (!occurrenceIds.length) return;

    const since = new Date(Date.now() - 48 * 3600_000).toISOString();
    const { data: delivered } = await supabase
      .from('reminder_delivery_log')
      .select('occurrence_id')
      .eq('user_id', userId)
      .gte('created_at', since)
      .in('occurrence_id', occurrenceIds);

    const deliveredSet = new Set((delivered || []).map((r: { occurrence_id: string }) => r.occurrence_id));
    const toCancel = pending.notifications
      .filter((n) => {
        const oid = (n.extra as { occurrenceId?: string } | null)?.occurrenceId;
        return oid && deliveredSet.has(oid);
      })
      .map((n) => ({ id: n.id }));

    if (toCancel.length > 0) {
      await LocalNotifications.cancel({ notifications: toCancel });
      logger.info(`[NativeReminders] Cancelled ${toCancel.length} already-delivered native reminder(s)`);
    }
  } catch (e) {
    logger.warn('[NativeReminders] cancelAlreadyDelivered failed', e);
  }
}

/**
 * Refill the rolling 7-day native reminder window for the current user.
 * Safe to call on app start, on login, and after any prescription/profile change.
 */
export async function refillNativeMedicationReminders(): Promise<void> {
  if (!isNative()) return;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const ok = await ensureChannelAndPermission();
  if (!ok) {
    logger.info('[NativeReminders] permission not granted, skipping schedule');
    return;
  }

  const [{ data: profile }, { data: prescriptions }] = await Promise.all([
    supabase
      .from('profiles')
      .select('id, timezone, travel_timezone, travel_active')
      .eq('id', user.id)
      .maybeSingle(),
    supabase
      .from('prescriptions')
      .select('id, user_id, medication_name, dosage, reminders, reminder_days_of_week, schedule_mode')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .not('reminders', 'is', null),
  ]);

  if (!profile) return;

  const planned = planOccurrencesForUser(
    profile as unknown as ProfileRow,
    (prescriptions || []) as unknown as PrescriptionRow[],
  );

  // Cancel any previously-scheduled medication reminders for this user, then re-schedule.
  // Reschedule using deterministic IDs ensures replace-not-duplicate semantics.
  try {
    const pending = await LocalNotifications.getPending();
    const ours = pending.notifications.filter(
      (n) => (n.extra as { type?: string } | null)?.type === 'medication_reminder_native',
    );
    if (ours.length > 0) {
      await LocalNotifications.cancel({ notifications: ours.map((n) => ({ id: n.id })) });
    }
  } catch (e) {
    logger.warn('[NativeReminders] failed to clear previous pending', e);
  }

  if (planned.length === 0) {
    logger.info('[NativeReminders] no future reminders to schedule');
    return;
  }

  // Capacitor caps each schedule call; chunk to be safe.
  const CHUNK = 50;
  for (let i = 0; i < planned.length; i += CHUNK) {
    const slice = planned.slice(i, i + CHUNK);
    try {
      await LocalNotifications.schedule({
        notifications: slice.map((p) => ({
          id: p.notificationId,
          title: '💊 Medication Reminder',
          body: `Time to take ${p.prescription.medication_name}${p.prescription.dosage ? ` (${p.prescription.dosage})` : ''}`,
          schedule: { at: p.fireAt, allowWhileIdle: true },
          channelId: Capacitor.getPlatform() === 'android' ? CHANNEL_ID : undefined,
          extra: {
            type: 'medication_reminder_native',
            occurrenceId: p.occurrenceId,
            prescriptionId: p.prescription.id,
            reminderTime: p.reminderTime,
            scheduledDate: p.scheduledDate,
            firedInTimezone: p.effectiveTz,
            link: '/dashboard?tab=meds',
          },
        })),
      });
    } catch (e) {
      logger.warn('[NativeReminders] schedule chunk failed', e);
    }
  }

  logger.info(
    `[NativeReminders] Scheduled ${planned.length} offline reminder(s) across ${ROLLING_DAYS}-day window`,
  );

  // Immediately prune any that the server already delivered.
  await cancelAlreadyDelivered(user.id);
}

/** Drop everything we scheduled (e.g. on logout). */
export async function clearNativeMedicationReminders(): Promise<void> {
  if (!isNative()) return;
  try {
    const pending = await LocalNotifications.getPending();
    const ours = pending.notifications.filter(
      (n) => (n.extra as { type?: string } | null)?.type === 'medication_reminder_native',
    );
    if (ours.length > 0) {
      await LocalNotifications.cancel({ notifications: ours.map((n) => ({ id: n.id })) });
    }
  } catch (e) {
    logger.warn('[NativeReminders] clear failed', e);
  }
}

/** Call on foreground / when push delivery log may have new rows. */
export async function pruneNativeRemindersAgainstServerDelivery(): Promise<void> {
  if (!isNative()) return;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  await cancelAlreadyDelivered(user.id);
}
